import React, { useState, useRef, useCallback, useEffect } from 'react';
import { proHeaders } from '../../utils/proApi';
import { BASE_DEPOT, MAX_FICHIERS, lireJson, deposerDirect, suivreTache, plageDe, duree } from '../../utils/deviseur';
import { Etiquette, Icone } from '../../components/pro/ui';

// Le relevé de visite tel qu'on le rapporte : dictées audio ou vidéo, photos
// (dont les plans), cotes en vrac. Déposé ici, il part en analyse — tâche de
// fond suivie pas à pas — et devient le dossier du devis.
//
// Un seul composant pour les deux portes d'entrée : la création d'un devis
// (« Nouveau devis », avec la fiche client au-dessus) et un devis existant
// sans dossier (écran Ressources), où `refExistant` rattache l'analyse au
// lead au lieu d'en créer un.

// L'exemple montre la syntaxe sans avoir l'air d'un brouillon : une pièce
// nommée, ses deux cotes, sa hauteur en dessous — et une pièce sans hauteur,
// pour dire qu'on peut l'omettre. Vérifié contre l'analyseur : trois pièces
// reconnues, aucune ligne ignorée.
const PLACEHOLDER_COTES = [
  'Séjour 4,20 x 3,60',
  'H 2,50',
  '',
  'Cuisine 3,10 x 2,80',
  '',
  'Couloir 3,90 x 1,20',
  'H 2,45',
].join('\n');

/**
 * @param {object} p
 * @param {object} p.chantier        la fiche saisie (prenom, nom, adresse, typeBien, etat, tva…)
 * @param {string} [p.refExistant]   rattacher l'analyse à ce lead plutôt qu'en créer un
 * @param {Function} p.onTermine     reçoit le résultat de l'analyse ({ ref, … })
 * @param {string} [p.libelle]       texte du bouton
 * @param {boolean} [p.bloque]       empêche le lancement (fiche incomplète), avec la raison en `raisonBlocage`
 */
export default function ReleveDeposeur({ chantier, refExistant, onTermine, libelle = 'Analyser le relevé et créer le devis', bloque = false, raisonBlocage = '' }) {
  const [fichiers, setFichiers] = useState([]);
  const [cotes, setCotes] = useState('');
  const [survol, setSurvol] = useState(false);
  const [analyse, setAnalyse] = useState(false);
  const [erreur, setErreur] = useState('');
  const [etape, setEtape] = useState('');
  const [pct, setPct] = useState(0);
  const [debutAnalyse, setDebutAnalyse] = useState(0);
  const [ecoule, setEcoule] = useState(0);
  const inputRef = useRef(null);

  // La barre suit l'étape : point exact quand il y en a un, sinon elle repart
  // du début de la plage. Elle ne recule jamais.
  useEffect(() => {
    if (!analyse) return;
    const { debut, exact } = plageDe(etape);
    setPct(p => Math.max(p, exact ?? debut));
  }, [etape, analyse]);

  // Entre deux étapes, elle rampe vers la fin de sa plage (jamais jusqu'au bout),
  // et le chrono tourne.
  useEffect(() => {
    if (!analyse) return undefined;
    const t = setInterval(() => {
      const { fin } = plageDe(etape);
      setPct(p => (p < fin - 1 ? p + Math.max(0.15, (fin - 1 - p) * 0.04) : p));
      setEcoule(Math.round((Date.now() - debutAnalyse) / 1000));
    }, 500);
    return () => clearInterval(t);
  }, [analyse, etape, debutAnalyse]);

  const ajouter = useCallback((liste) => {
    const nouveaux = Array.from(liste).filter(f => f.size > 0);
    setFichiers(f => [...f, ...nouveaux].slice(0, MAX_FICHIERS));
  }, []);

  const onDrop = (e) => {
    e.preventDefault(); setSurvol(false);
    ajouter(e.dataTransfer.files);
    const texte = e.dataTransfer.getData('text');
    if (texte && !cotes) setCotes(texte);
  };

  const lancer = async () => {
    setAnalyse(true); setErreur(''); setEtape('Envoi des fichiers');
    setPct(0); setEcoule(0); setDebutAnalyse(Date.now());
    try {
      const url = `${BASE_DEPOT}/api/pro/releve/ingerer`;
      let r;
      const cles = await deposerDirect(fichiers, proHeaders(), setEtape);
      if (cles || (BASE_DEPOT && !fichiers.length)) {
        r = await fetch(url, {
          method: 'POST', headers: proHeaders(),
          body: JSON.stringify({ cotes, chantier, fichiers: cles || [], ...(refExistant ? { ref: refExistant } : {}) }),
        });
      } else {
        const form = new FormData();
        fichiers.forEach(f => form.append('fichiers', f));
        form.append('cotes', cotes);
        form.append('chantier', JSON.stringify(chantier));
        if (refExistant) form.append('ref', refExistant);
        // FormData pose son propre Content-Type (avec le boundary) : on reprend
        // l'authentification sans lui.
        const { 'Content-Type': _ct, ...entetes } = proHeaders();
        r = await fetch(url, { method: 'POST', headers: entetes, body: form });
      }
      const depot = await lireJson(r);
      if (!r.ok) throw new Error(depot.error || `Dépôt refusé (${r.status})`);
      setEtape('Préparation');   // le serveur a la main ; le premier suivi arrive dans deux secondes

      // L'analyse tourne côté serveur ; le proxy coupe toute requête à 26 s,
      // donc on ne l'attend pas : on suit la tâche jusqu'au résultat.
      const resultat = await suivreTache(depot.tache, proHeaders(), setEtape);
      setPct(100);
      setFichiers([]);
      onTermine(resultat);
    } catch (e) { setErreur(e.message); }
    finally { setAnalyse(false); setEtape(''); }
  };

  const audio = fichiers.filter(f => /^(audio|video)\//.test(f.type));
  const images = fichiers.filter(f => /^image\//.test(f.type));
  const rienADeposer = !fichiers.length && !cotes.trim();

  return (
    <div>
      <div className="grid gap-4 lg:grid-cols-2 items-stretch">
        <div className="flex flex-col">
          <div
            onDragOver={e => { e.preventDefault(); setSurvol(true); }}
            onDragLeave={() => setSurvol(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`flex-1 flex flex-col items-center justify-center border border-dashed rounded-xl px-6 py-9 text-center cursor-pointer transition-colors ${survol ? 'border-orange-500 bg-orange-50/60' : 'border-slate-300 bg-slate-50/60 hover:border-slate-400 hover:bg-slate-50'}`}
          >
            <div className="flex items-center justify-center gap-3 text-slate-300 mb-3">
              <Icone nom="micro" className="w-6 h-6" />
              <Icone nom="video" className="w-6 h-6" />
              <Icone nom="photo" className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-slate-700">Déposez vos fichiers ici</p>
            <p className="text-xs text-slate-400 mt-1">Dictées audio ou vidéo, photos de la visite et plans — jusqu'à 40 fichiers, 200 Mo chacun</p>
            <input ref={inputRef} type="file" multiple className="hidden"
                   accept="audio/*,video/*,image/*"
                   onChange={e => { ajouter(e.target.files); e.target.value = ''; }} />
          </div>

          {fichiers.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {fichiers.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-xs text-slate-600">
                  <Icone nom={/^image\//.test(f.type) ? 'photo' : /^video\//.test(f.type) ? 'video' : 'micro'} className="w-3.5 h-3.5 text-slate-400" />
                  {f.name.length > 28 ? f.name.slice(0, 26) + '…' : f.name}
                  <button onClick={e => { e.stopPropagation(); setFichiers(l => l.filter((_, j) => j !== i)); }}
                          className="text-slate-300 hover:text-red-500">✕</button>
                </span>
              ))}
            </div>
          )}
        </div>

        <label className="flex flex-col">
          <Etiquette className="mb-1.5">Cotes relevées</Etiquette>
          <textarea
            value={cotes} onChange={e => setCotes(e.target.value)} rows={8}
            placeholder={PLACEHOLDER_COTES}
            className="w-full champ font-mono text-xs leading-relaxed" />
          <span className="block text-xs text-slate-400 mt-1.5">
            Telles que vous les notez. « 4 sur 3 », « 4x3 », et une hauteur seule se rattache à la pièce du dessus. Un plan coté photographié suffit aussi.
          </span>
        </label>
      </div>

      <div className="mt-5 pt-4 border-t border-slate-100">
        <button
          onClick={lancer}
          disabled={analyse || rienADeposer || bloque}
          title={bloque ? raisonBlocage : ''}
          className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-slate-100 disabled:text-slate-400 text-white font-semibold py-3 rounded-lg transition-colors">
          {analyse ? (
            <span className="inline-flex items-center gap-2">
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
                <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              </svg>
              Analyse en cours…
            </span>
          ) : libelle}
        </button>

        {analyse && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4" aria-live="polite">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate">{etape || 'Analyse en cours'}…</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {duree(ecoule)}
                  {audio.length ? ` · ${audio.length} dictée${audio.length > 1 ? 's' : ''}` : ''}
                  {images.length ? ` · ${images.length} photo${images.length > 1 ? 's' : ''}` : ''}
                  {' · '}la transcription et l'inspection des photos sont les étapes longues
                </p>
              </div>
              <span className="text-sm font-semibold tabular-nums text-slate-700 shrink-0">{Math.round(pct)} %</span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-slate-200 overflow-hidden" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full rounded-full barre-anim transition-[width] duration-500 ease-out" style={{ width: `${Math.max(2, pct)}%` }} />
            </div>
          </div>
        )}
        {!analyse && rienADeposer && (
          <p className="mt-2.5 text-xs text-slate-400 text-center">
            Une dictée, des photos, des cotes : il faut au moins l'un des trois pour lancer.
          </p>
        )}
        {!analyse && bloque && !rienADeposer && raisonBlocage && (
          <p className="mt-2.5 text-xs text-amber-700 text-center">{raisonBlocage}</p>
        )}
        {erreur && <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{erreur}</p>}
      </div>
    </div>
  );
}
